export enum FeatureAvailability {
  ENTERPRISE = 'enterprise',
  PRO = 'pro',
  PLAN_DEPENDENT = 'plan-dependent'
}

export const FEATURE_CATEGORIES = [
  {
    id: 'development',
    name: 'Development tools',
    description: 'Write, collaborate on, and release code.'
  },
  {
    id: 'data',
    name: 'Data & backend',
    description: 'Model, query, synchronize, and manage your data.'
  },
  {
    id: 'automation',
    name: 'Automation & integrations',
    description: 'Run server-side workflows and connect systems.'
  },
  {
    id: 'runtime',
    name: 'Runtime & UI',
    description: 'Build interactive applications and reusable interfaces.'
  },
  {
    id: 'devices',
    name: 'Devices & distribution',
    description: 'Run apps across devices and connect to hardware.'
  },
  {
    id: 'administration',
    name: 'Administration & access',
    description: 'Manage projects, identities, and platform access.'
  }
] as const;

export type FeatureCategoryId = (typeof FEATURE_CATEGORIES)[number]['id'];

export enum FeatureNodeType {
  INTERFACE = 'interface',
  CLOUD_SERVICE = 'cloudService',
  RUNTIME = 'runtime',
  TOOL = 'tool',
  FEATURE = 'feature'
}

export const FEATURE_NODE_LABELS: Record<FeatureNodeType, string> = {
  [FeatureNodeType.INTERFACE]: 'Interface',
  [FeatureNodeType.CLOUD_SERVICE]: 'Cloud service',
  [FeatureNodeType.RUNTIME]: 'Runtime technology',
  [FeatureNodeType.TOOL]: 'Developer tool',
  [FeatureNodeType.FEATURE]: 'Feature'
};

export const FEATURE_SYSTEMS = [
  {
    id: 'oxide',
    name: 'OXIDE',
    description: 'Develop views, schemas, and CloudCode tasks with code and visual tools.',
    links: {
      product: '/oxide/',
      docs: '/docs/oxide/'
    },
    type: FeatureNodeType.INTERFACE,
    connectsTo: ['source', 'build', 'deployments', 'cloudcode', 'backend', 'diagnostics', 'distribution']
  },
  {
    id: 'cloudcode',
    name: 'CloudCode',
    description: 'Run server-side tasks for automation, integrations, and application logic.',
    links: {
      product: '/cloud-code/',
      docs: '/docs/cloudcode/cloudcode-overview/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: ['backend', 'diagnostics']
  },
  {
    id: 'backend',
    name: 'App Backend',
    description:
      'Store and synchronize data, manage files and attachments, record audit logs, and handle device enrollment.',
    links: {
      product: '/app-backend/',
      docs: '/docs/concepts/backend-data/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'runtime',
    name: 'Runtime Engine',
    description: 'Run application interfaces and logic across supported devices.',
    links: {
      product: '/runtime-engine/',
      docs: '/docs/runtime/'
    },
    type: FeatureNodeType.RUNTIME,
    connectsTo: ['backend', 'updates']
  },
  {
    id: 'container',
    name: 'Runtime Container',
    description: 'Run applications in native mobile and desktop containers with access to device capabilities.',
    links: {
      docs: '/docs/container/'
    },
    type: FeatureNodeType.RUNTIME,
    connectsTo: ['runtime', 'backend', 'updates']
  },
  {
    id: 'web-container',
    name: 'Web Container',
    description: 'Run applications in a supported desktop web browser without installing a native container.',
    links: {
      docs: '/docs/container/supported-platforms/web-container/'
    },
    type: FeatureNodeType.RUNTIME,
    connectsTo: ['runtime', 'backend', 'updates']
  },
  {
    id: 'override',
    name: 'OVERRIDE',
    description: 'Manage organizations, projects, platform access, and authentication.',
    links: {
      product: '/override/',
      docs: '/docs/override/'
    },
    type: FeatureNodeType.INTERFACE,
    connectsTo: ['identity']
  },
  {
    id: 'sector',
    name: 'Sector',
    description: 'Browse and edit live records, and investigate data changes.',
    links: {
      product: '/sector/',
      docs: '/docs/sector/'
    },
    type: FeatureNodeType.INTERFACE,
    connectsTo: ['backend']
  },
  {
    id: 'cli',
    name: 'Platform CLI',
    description: 'Work with platform source and operations from terminals, pipelines, and AI tools.',
    links: {
      docs: '/docs/platform-cli/'
    },
    type: FeatureNodeType.TOOL,
    connectsTo: ['source', 'build', 'deployments']
  },
  {
    id: 'source',
    name: 'Source management',
    description: 'Manage project code, drafts, version history, and collaborative changes.',
    links: {
      docs: '/docs/oxide/source-and-drafts/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'build',
    name: 'Build system',
    description: 'Turn project source and packages into runnable artifacts.',
    links: {
      docs: '/docs/oxide/debugging/build-logs/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'deployments',
    name: 'Deployment management',
    description: 'Release project versions to the intended testing, staging, and production deployments.',
    links: {
      docs: '/docs/oxide/testing-and-releases/release/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: ['build', 'updates']
  },
  {
    id: 'updates',
    name: 'Application updates',
    description: 'Deliver deployed application changes and Runtime updates to apps.',
    links: {
      docs: '/docs/container/app-runtime-and-container-updates/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'identity',
    name: 'Identity and access',
    description: 'Manage platform permissions, app identities, and sign-in.',
    links: {
      docs: '/docs/concepts/users-and-access/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'diagnostics',
    name: 'Diagnostics',
    description: 'Investigate build failures, task execution, and device behavior.',
    links: {
      docs: '/docs/oxide/debugging/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: []
  },
  {
    id: 'distribution',
    name: 'Application distribution',
    description: 'Build, brand, sign, and distribute application containers.',
    links: {
      docs: '/docs/oxide/create-and-manage-app-containers/'
    },
    type: FeatureNodeType.CLOUD_SERVICE,
    connectsTo: ['container']
  }
] as const;

export type FeatureSystemId = (typeof FEATURE_SYSTEMS)[number]['id'];

export interface PlatformFeature {
  id: string;
  name: string;
  description: string;
  category: FeatureCategoryId;
  systems: readonly FeatureSystemId[];
  links?: { docs?: string; product?: string };
  tags: readonly string[];
  availability: FeatureAvailability;
}

/**
 * Shared feature content and system relationships for the site. Plan-dependent entries are not claims of availability on every plan.
 */
export const PLATFORM_FEATURES: readonly PlatformFeature[] = [
  {
    id: 'app-roles-and-data-access',
    name: 'App roles and data access',
    description: 'Define app roles and data rules to control which records users can access.',
    category: 'administration',
    links: {
      docs: '/docs/data-model/data-rules/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime', 'backend']
  },
  {
    id: 'custom-roles',
    name: 'Custom roles',
    description: 'Group policies into reusable roles and assign them to platform users.',
    category: 'administration',
    links: { docs: '/docs/override/organization-administration/custom-roles-and-policies/#create-and-populate-a-role' },
    tags: ['administration', 'roles', 'access'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['identity']
  },
  {
    id: 'custom-policies',
    name: 'Custom policies',
    description: 'Apply organization-specific policy controls for governance, compliance and platform administration.',
    category: 'administration',
    links: {
      docs: '/docs/override/organization-administration/custom-roles-and-policies/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['identity']
  },
  {
    id: 'policy-editor',
    name: 'Policy editor',
    description: 'Create and edit scoped policies in OVERRIDE using the JSON editor and its validation.',
    category: 'administration',
    links: { docs: '/docs/override/organization-administration/custom-roles-and-policies/#create-a-policy' },
    tags: ['administration', 'policies', 'access'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['override']
  },
  {
    id: 'audit-review',
    name: 'Audit review',
    description: 'Review audit information through the OVERRIDE administration interface.',
    category: 'administration',
    links: { product: '/override/' },
    tags: ['administration', 'audit', 'governance'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['override']
  },
  {
    id: 'team-administration',
    name: 'Team administration',
    description: 'Manage developer invitations and organization roles in OVERRIDE.',
    category: 'administration',
    links: { docs: '/docs/override/organization-administration/team/' },
    tags: ['administration', 'team', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['override']
  },
  {
    id: 'team-access',
    name: 'Team access',
    description: 'Invite developers and assign roles to control access to organizations and projects.',
    category: 'administration',
    links: {
      docs: '/docs/override/organization-administration/team/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['identity']
  },
  {
    id: 'usage-reporting',
    name: 'Usage reporting',
    description: 'Review subscription usage and billing in OVERRIDE.',
    category: 'administration',
    links: {
      docs: '/docs/override/billing-and-plans/'
    },
    tags: ['administration'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['override']
  },
  {
    id: 'organization-sso',
    name: 'Organization SSO',
    description:
      'Integrate your organization with enterprise identity providers for streamlined sign-in and account management.',
    category: 'administration',
    links: {
      docs: '/docs/override/configure-sso/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['identity']
  },
  {
    id: 'external-git-repositories',
    name: 'External Git repositories',
    description:
      'Keep project source in your own GitHub or Azure DevOps repository and work with it using local editors and Git tools.',
    category: 'development',
    links: {
      docs: '/docs/get-started/create-a-project/git-providers-and-integrations/'
    },
    tags: ['development', 'git', 'source'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['source']
  },
  {
    id: 'github-integration',
    name: 'GitHub integration',
    description: 'Connect projects to GitHub repositories for source control and collaboration.',
    category: 'development',
    links: {
      docs: '/docs/get-started/create-a-project/git-providers-and-integrations/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PRO,
    systems: ['source']
  },
  {
    id: 'azure-devops-integration',
    name: 'Azure DevOps integration',
    description: 'Use Azure DevOps repositories for project source control.',
    category: 'development',
    links: {
      docs: '/docs/get-started/create-a-project/git-providers-and-integrations/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['source']
  },
  {
    id: 'external-webhooks',
    name: 'External webhooks',
    description: 'Notify external systems when backend records are created, updated, or deleted.',
    category: 'automation',
    links: {
      docs: '/docs/data-model/webhooks-external/'
    },
    tags: ['automation'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'opsql-data-pipelines',
    name: 'OPSQL data pipelines',
    description: 'Build SQL-based data pipelines for analytics, reporting and downstream integrations.',
    category: 'automation',
    links: {},
    tags: ['automation'],
    availability: FeatureAvailability.PRO,
    systems: []
  },
  {
    id: '2fa',
    name: '2FA',
    description: 'Strengthen platform access with two-factor authentication for organization users.',
    category: 'administration',
    links: {
      docs: '/docs/override/profile/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['identity']
  },
  {
    id: 'flexible-plans-and-pricing',
    name: 'Flexible plans and pricing',
    description: 'Choose a plan for your project and review usage as your needs grow.',
    category: 'administration',
    links: {
      product: '/pricing/'
    },
    tags: ['administration'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: []
  },
  {
    id: 'soc-2-audited',
    name: 'SOC 2 audited',
    description: 'Review the platform’s security and compliance information for your procurement process.',
    category: 'administration',
    links: {
      product: '/overview/faq/'
    },
    tags: ['administration'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: []
  },
  {
    id: 'live-co-editing',
    name: 'Live co-editing',
    description:
      'Developers can work on the same code files at the same time with real-time collaboration. Developers can even invite external users to the same coding session.',
    category: 'development',
    links: {
      docs: '/docs/oxide/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['source']
  },
  {
    id: 'full-git-support-with-conflict-resolution',
    name: 'Advanced Git support',
    description:
      'Work with separate drafts, branches, and conflict resolution in Advanced version-control mode, using platform-hosted or external Git.',
    category: 'development',
    links: {
      docs: '/docs/get-started/create-a-project/git-providers-and-integrations/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['source']
  },
  {
    id: 'code-analysis',
    name: 'Code analysis',
    description: 'Analyze app code and configuration to surface issues earlier during development.',
    category: 'development',
    links: {},
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'javascript',
    name: 'JavaScript',
    description: 'Write application logic in JavaScript.',
    category: 'development',
    links: {
      docs: '/docs/get-started/syntax/'
    },
    tags: ['development', 'runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    description: 'Use TypeScript for typed application code and shared modules.',
    category: 'development',
    links: {
      docs: '/docs/concepts/projects/typescript-projects/'
    },
    tags: ['development', 'runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'cloudcode-javascript',
    name: 'JavaScript',
    description: 'Write server-side tasks and integrations in JavaScript.',
    category: 'development',
    links: {
      docs: '/docs/cloudcode/cloudcode-overview/'
    },
    tags: ['development', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'cloudcode-typescript',
    name: 'TypeScript',
    description: 'Use TypeScript for typed server-side tasks and integrations.',
    category: 'development',
    links: {
      docs: '/docs/cloudcode/cloudcode-overview/'
    },
    tags: ['development', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'xml',
    name: 'XML',
    description: 'Define views, components and bindings using JourneyApps XML syntax.',
    category: 'development',
    links: {
      docs: '/docs/get-started/syntax/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime', 'backend']
  },
  {
    id: 'vs-code-themes',
    name: 'VS Code themes',
    description: 'Customize OXIDE with built-in themes or import a VS Code editor theme.',
    category: 'development',
    links: {
      docs: '/docs/guides/workspaces-and-actions/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'custom-shortcuts-optional-vi-mode',
    name: 'Custom shortcuts & optional VI mode',
    description: 'Use keyboard shortcuts and optional VI mode to work in the editor.',
    category: 'development',
    links: {
      docs: '/docs/guides/workspaces-and-actions/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'open-source-sdks-libraries-and-frameworks',
    name: 'Open-source SDKs, libraries and frameworks',
    description: 'Extend the platform with SDKs, packages and custom code built on open standards and tooling.',
    category: 'development',
    links: {},
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: []
  },
  {
    id: 'continuous-integration-and-build-servers',
    name: 'Continuous integration and build servers',
    description: 'Use the Platform CLI to automate source and deployment operations in your build pipelines.',
    category: 'development',
    links: {
      docs: '/docs/platform-cli/source-and-deployments/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cli', 'build', 'deployments']
  },
  {
    id: 'deploy-history',
    name: 'Deploy history',
    description: 'Review deployment history across environments to understand what changed and when.',
    category: 'development',
    links: {
      docs: '/docs/oxide/testing-and-releases/release/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['deployments']
  },
  {
    id: 'diagnostics-reports-and-build-logs',
    name: 'Diagnostics reports and build logs',
    description: 'Investigate app issues using build logs, diagnostics reports and troubleshooting tools in OXIDE.',
    category: 'development',
    links: {
      docs: '/docs/oxide/debugging/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['diagnostics']
  },
  {
    id: 'visual-editing',
    name: 'Visual editing',
    description: 'Edit views and schemas visually while working with their underlying XML.',
    category: 'development',
    links: {
      docs: '/docs/oxide/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'drag-and-drop',
    name: 'Drag and drop',
    description: 'Arrange view components with drag-and-drop editing in OXIDE.',
    category: 'development',
    links: {
      docs: '/docs/oxide/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'indexed-code-search',
    name: 'Indexed code search',
    description: 'Find code across your project from OXIDE.',
    category: 'development',
    links: {},
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'smart-autocomplete-and-intellisense',
    name: 'Smart autocomplete and IntelliSense',
    description: 'Get code suggestions and type information while editing.',
    category: 'development',
    links: {},
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['oxide']
  },
  {
    id: 'granular-schema-field-types',
    name: 'Granular schema field types',
    description: 'Model app data using a rich set of field types for text, files, locations, relationships and more.',
    category: 'data',
    links: {
      docs: '/docs/data-model/model/field/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'view-templates',
    name: 'View templates',
    description: 'Reuse view structures and interactions with templates that keep screens consistent and maintainable.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/view-templates/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'dedicated-tenancy',
    name: 'Dedicated tenancy',
    description:
      'Run your backend in a dedicated capacity on JourneyApps infrastructure, with database and file storage provided by default.',
    category: 'data',
    links: {
      product: '/app-backend/'
    },
    tags: ['data'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['backend']
  },
  {
    id: 'testing-staging-and-production-deployments',
    name: 'Testing, Staging and Production deployments',
    description:
      'Promote changes through testing, staging and production deployments with environment-specific controls.',
    category: 'data',
    links: {
      docs: '/docs/oxide/testing-and-releases/configuring-testing-deployments/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['deployments']
  },
  {
    id: 'elevated-access-controls',
    name: 'Elevated access controls',
    description: 'Protect sensitive backend operations with stricter operational access controls and permissions.',
    category: 'data',
    links: {},
    tags: ['data', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'backend-api',
    name: 'Backend API',
    description: 'Query and manipulate app data securely from external systems and server-side workflows.',
    category: 'data',
    links: {
      docs: '/docs/backend-api/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'app-indexes',
    name: 'App indexes',
    description: 'Improve query performance with custom indexes defined in your app data model.',
    category: 'data',
    links: {
      docs: '/docs/data-model/app-indexing/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'fine-tuned-rate-limiting',
    name: 'Fine-tuned rate-limiting',
    description:
      'Configure backend limits and throughput controls to suit different deployment and tenant requirements.',
    category: 'data',
    links: {},
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'audit-logs',
    name: 'Audit logs',
    description: 'Investigate recorded data operations through backend audit logs.',
    category: 'data',
    links: {
      docs: '/docs/sector/record-history/'
    },
    tags: ['data', 'access'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['backend']
  },
  {
    id: 'change-logs',
    name: 'Change logs',
    description: 'Inspect recorded changes to understand how data has evolved.',
    category: 'data',
    links: {
      docs: '/docs/sector/record-history/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'data-model-relationships',
    name: 'Data model relationships',
    description: 'Define relationships between models and work with related records.',
    category: 'data',
    links: {
      docs: '/docs/data-model/model/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'batch-operations',
    name: 'Batch operations',
    description: 'Create, update and delete multiple records efficiently using batch API operations.',
    category: 'data',
    links: {
      docs: '/docs/backend-api/api-reference/batch-operations-v4-api/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'bulk-import-and-export',
    name: 'Bulk import and export',
    description: 'Import and export structured data in bulk for integrations, migration and operational workflows.',
    category: 'data',
    links: {
      docs: '/docs/runtime/js-ts-apis/csv/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime', 'sector']
  },
  {
    id: 'app-and-device-sessions',
    name: 'App and device sessions',
    description: 'Manage app users, authentication sessions and related backend session behavior.',
    category: 'administration',
    links: {
      docs: '/docs/backend-api/api-reference/manage-app-users-and-sessions/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'web-browser-access',
    name: 'Browser access',
    description:
      'Open applications in supported desktop browsers. Web access is enabled on request, with capabilities that differ from native containers.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/web-container/'
    },
    tags: ['web', 'browser'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['web-container']
  },
  {
    id: 'web-custom-domains',
    name: 'Custom domains and HTTPS',
    description:
      'Use your own domain for a web app, with an automatically provisioned HTTPS certificate. Domain setup is arranged through support.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/web-container/#where-will-users-access-the-app'
    },
    tags: ['web', 'browser'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['web-container']
  },
  {
    id: 'web-favicons',
    name: 'Custom favicons',
    description: 'Set a container favicon or override it with an app-level icon configured in OXIDE.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/web-container/#custom-web-container-favicon'
    },
    tags: ['web', 'browser'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['web-container']
  },
  {
    id: 'web-multiple-tabs',
    name: 'Multiple browser tabs',
    description: 'Open the same application in multiple browser tabs.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/web-container/#can-the-same-application-be-opened-in-multiple-tabs'
    },
    tags: ['web', 'browser'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['web-container']
  },
  {
    id: 'desktop-support',
    name: 'Desktop support',
    description:
      'Run applications on the desktop with support for macOS, Windows and Linux. Applications can access the native file system and other native APIs with automatic updates.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/'
    },
    tags: ['devices'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'ios-ipados',
    name: 'iOS & iPadOS',
    description: 'Run applications on supported iPhones and iPads with access to native capabilities.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/'
    },
    tags: ['devices'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'android',
    name: 'Android',
    description: 'Run applications on supported Android devices.',
    category: 'devices',
    links: {
      docs: '/docs/container/supported-platforms/'
    },
    tags: ['devices'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'realwear',
    name: 'RealWear',
    description: 'Run XR enabled applications on RealWear devices.',
    category: 'devices',
    links: {
      docs: '/docs/integrations/realwear-r-voice-control/'
    },
    tags: ['devices', 'hardware', 'experimental'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'custom-branded-builds',
    name: 'Custom branded builds',
    description: 'Distribute customer-facing apps with your own branding, assets and container identity.',
    category: 'devices',
    links: {},
    tags: ['devices'],
    availability: FeatureAvailability.PRO,
    systems: ['distribution']
  },
  {
    id: 'custom-provisioning-profiles',
    name: 'Custom provisioning profiles',
    description: 'Control app signing and provisioning for enterprise and app-store distribution workflows.',
    category: 'devices',
    links: {},
    tags: ['devices'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['distribution']
  },
  {
    id: 'external-sso',
    name: 'External SSO',
    description: 'Integrate external identity and sign-in flows for apps that need enterprise authentication.',
    category: 'administration',
    links: {
      docs: '/docs/override/configure-sso/'
    },
    tags: ['administration', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['identity']
  },
  {
    id: 'crash-reporting',
    name: 'Crash reporting',
    description: 'Collect device diagnostics to help investigate application crashes.',
    category: 'devices',
    links: {
      docs: '/docs/container/device-diagnostics/'
    },
    tags: ['devices'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['diagnostics']
  },
  {
    id: 'nfc',
    name: 'NFC',
    description: 'Interact with NFC-enabled devices and tags from inside your app.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/nfc/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.PRO,
    systems: ['container']
  },
  {
    id: 'serial-port-access',
    name: 'Serial port access',
    description: 'Communicate with serial devices directly from supported runtime containers.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/serialport/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'bluetooth-low-energy-ble',
    name: 'Bluetooth Low Energy (BLE)',
    description: 'Connect to BLE peripherals for scanning, communication and device workflows.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/bluetooth-low-energy/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'barcode-scanning',
    name: 'Barcode scanning',
    description: 'Capture barcode and QR code values using built-in scanning components and APIs.',
    category: 'devices',
    links: {
      docs: '/docs/integrations/hardware/barcode-scanning/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'tcp-sockets',
    name: 'TCP sockets',
    description: 'Open raw TCP socket connections for custom device and network integrations.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/tcp-sockets/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'gps-capture-and-tracking',
    name: 'GPS capture and tracking',
    description: 'Capture locations, work with coordinates and record GPS tracks from supported devices.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/gps-capturing/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['container']
  },
  {
    id: 'journeyapps-print',
    name: 'JourneyApps Print',
    description: 'Print documents and generated output directly from supported runtime containers and integrations.',
    category: 'devices',
    links: {
      docs: '/docs/integrations/hardware/journeyapps-print-android/'
    },
    tags: ['devices', 'hardware'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'modular-extensible-architecture',
    name: 'Modular, extensible architecture',
    description: 'Extend applications with shared code, packages, and integrations.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/extending-your-app-with-custom-code/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'global-runtime-apis',
    name: 'Global Runtime APIs',
    description:
      'Work with data, navigation, components, and device capabilities through JavaScript and TypeScript APIs.',
    category: 'runtime',
    links: {
      docs: '/docs/get-started/syntax/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'offline-first-data-sync',
    name: 'Offline-first data sync',
    description: 'Keep app data synchronized across devices and the cloud with offline-first sync behavior.',
    category: 'data',
    links: {
      docs: '/docs/data-model/data-rules/'
    },
    tags: ['data', 'offline'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'data-rules-and-acls',
    name: 'Data rules and ACLs',
    description: 'Control synced data, bucket structure and access rules for secure offline-first applications.',
    category: 'data',
    links: {
      docs: '/docs/data-model/data-rules/'
    },
    tags: ['data', 'access', 'offline'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['backend']
  },
  {
    id: 'ui-component-library',
    name: 'UI component library',
    description: 'Build interfaces with reusable Runtime UI components.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'object-table',
    name: 'Object table',
    description: 'Present, edit and manage rich tabular data with advanced object-table interactions and controls.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/object-table/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'file-capture-and-display',
    name: 'File capture and display',
    description: 'Capture, store and present files directly in app workflows.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/capture-file/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'signature-capture-and-display',
    name: 'Signature capture and display',
    description: 'Collect signatures and display signed content as part of mobile and field workflows.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/capture-signature/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'html-and-iframe-integration',
    name: 'HTML and iframe integration',
    description: 'Embed custom HTML, iframe content and interactive web experiences inside apps.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/html/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: '3d-model-display',
    name: '3D model display',
    description: 'Render and interact with 3D models directly in the runtime.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/display-3d-model/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['runtime']
  },
  {
    id: 'themes-and-app-styling',
    name: 'Themes and app styling',
    description: 'Customize the look and feel of your app with themes, branding and component styling controls.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/app-styling-and-customizations/component-styling-and-configuration/overview/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'voice-control',
    name: 'Voice control',
    description: 'Support hands-free workflows on compatible devices with voice-driven interactions.',
    category: 'runtime',
    links: {
      docs: '/docs/integrations/realwear-r-voice-control/'
    },
    tags: ['runtime', 'hardware'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'responsive-design',
    name: 'Responsive design',
    description: 'Adapt layouts and interactions across phones, tablets and desktop screens.',
    category: 'runtime',
    links: {
      docs: '/docs/get-started/tutorial-build-your-first-app/advanced-topics/layouts-styling-and-lists/responsive-layouts/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'app-packages',
    name: 'App packages',
    description: 'Extend apps with reusable packages, shared code and custom capabilities.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/app-packages/app-packages-overview/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'translations',
    name: 'Translations',
    description: 'Localize app content and support multiple languages across views and workflows.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/translations/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'upload-device-and-engine-logs',
    name: 'Upload device and engine logs',
    description: 'Capture diagnostics and logs from devices to support troubleshooting and support workflows.',
    category: 'runtime',
    links: {
      docs: '/docs/oxide/debugging/app-diagnostics-reports/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['diagnostics']
  },
  {
    id: 'http-requests-with-fetch-api',
    name: 'HTTP requests with Fetch API',
    description: 'Call external HTTP services from apps using the Fetch API.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/integration/http-requests-fetch-api/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'maps-and-navigation',
    name: 'Maps and navigation',
    description: 'Open maps, navigate externally and work with coordinates inside app workflows.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/integration/opening-external-links-apps/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'embedded-power-bi-dashboards',
    name: 'Embedded Power BI dashboards',
    description: 'Embed dashboards and Power BI experiences directly in JourneyApps applications.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/all-ui-components/power-bi/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'push-notifications',
    name: 'Push notifications',
    description: 'Notify users in real time with built-in push notification support.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/push-notifications/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'deep-linking',
    name: 'Deep linking',
    description: 'Open app views directly with links that include route and parameter context.',
    category: 'runtime',
    links: {
      docs: '/docs/runtime/view-navigation/deep-linking/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['runtime']
  },
  {
    id: 'external-npm-packages',
    name: 'External NPM packages',
    description: 'Use external packages in CloudCode tasks to integrate services and extend backend logic.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/code-and-integrations/cloudcode-dependencies/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'database-access',
    name: 'Database access',
    description: 'Read and write app data directly from CloudCode tasks.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/code-and-integrations/access-multiple-dbs-in-cloudcode-tasks/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode', 'backend']
  },
  {
    id: 'data-change-triggers',
    name: 'Data change triggers',
    description: 'Run CloudCode tasks when backend records are created, updated, or deleted.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/triggering-a-cloudcode-task/trigger-cc-via-a-webhook/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'scheduled-execution',
    name: 'Scheduled execution',
    description: 'Run CloudCode tasks on a schedule for background processing and automation.',
    category: 'automation',
    links: {
      docs: '/docs/oxide/cloudcode/schedules/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'high-availability',
    name: 'High availability',
    description: 'Execute CloudCode tasks on managed cloud infrastructure.',
    category: 'automation',
    links: {},
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'trigger-from-the-runtime',
    name: 'Trigger from the runtime',
    description: 'Call CloudCode tasks directly from the app runtime and return results to the user workflow.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/triggering-a-cloudcode-task/trigger-cc-from-an-app/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'egress-proxy-for-whitelisting',
    name: 'Egress proxy for whitelisting',
    description: 'Route outbound CloudCode traffic through controlled network paths for whitelisting and compliance.',
    category: 'automation',
    links: {},
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'custom-domain',
    name: 'Custom domain',
    description: 'Expose CloudCode endpoints behind custom domains for cleaner integration and deployment workflows.',
    category: 'automation',
    links: {},
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'pdf-reports-in-cloudcode',
    name: 'PDF reports in CloudCode',
    description: 'Generate PDF reports in CloudCode for exports, operational documents and automated workflows.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/code-and-integrations/pdf-reports-using-cloudcode/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'log-search',
    name: 'Log search',
    description: 'Search CloudCode logs to investigate backend behavior and troubleshoot production issues.',
    category: 'automation',
    links: {
      docs: '/docs/oxide/cloudcode/testing-and-logs/'
    },
    tags: ['automation', 'cloudcode'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['diagnostics']
  },
  {
    id: 'cli-source-and-operations',
    name: 'Source and deployment commands',
    description: 'Manage source, deployments, and platform operations from your terminal or automation pipeline.',
    category: 'development',
    links: {
      docs: '/docs/platform-cli/'
    },
    tags: ['cli', 'automation'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cli']
  },
  {
    id: 'ai-development',
    name: 'AI-assisted development',
    description: 'Connect AI development tools to project context and platform operations through the CLI.',
    category: 'development',
    links: {
      docs: '/docs/platform-cli/ai-workflows/',
      product: '/ai/'
    },
    tags: ['ai', 'cli'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cli']
  },
  {
    id: 'cloudcode-http',
    name: 'CloudCode HTTP endpoints',
    description:
      'Expose server-side functions over HTTP for incoming requests and integrations, without requiring an app interface.',
    category: 'automation',
    links: {
      docs: '/docs/cloudcode/triggering-a-cloudcode-task/trigger-cc-via-http/'
    },
    tags: ['cloudcode', 'http', 'api'],
    availability: FeatureAvailability.PRO,
    systems: ['cloudcode']
  },
  {
    id: 'cloudcode-queues',
    name: 'CloudCode execution queues',
    description: 'Inspect queued work for each deployment and pause, resume, or clear pending invocations.',
    category: 'automation',
    links: {
      docs: '/docs/oxide/cloudcode/queues/'
    },
    tags: ['cloudcode', 'operations'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['cloudcode']
  },
  {
    id: 'photonsync',
    name: 'PhotonSync',
    description: 'Transfer data between offline devices using flashing QR codes.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/photonsync-offline-data-transmission-x/'
    },
    tags: ['offline', 'hardware'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'hardware-barcode-scanning',
    name: 'Hardware barcode scanning',
    description: 'Use integrated barcode scanners on supported industrial devices.',
    category: 'devices',
    links: {
      docs: '/docs/runtime/js-ts-apis/hardware-barcode-scanning/'
    },
    tags: ['hardware', 'capture'],
    availability: FeatureAvailability.ENTERPRISE,
    systems: ['container']
  },
  {
    id: 'browse-and-edit-records',
    name: 'Browse and edit records',
    description: 'Find records, follow relationships, and review edits before saving.',
    category: 'data',
    links: {
      product: '/sector/',
      docs: '/docs/sector/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['sector']
  },
  {
    id: 'authentication-scope-management',
    name: 'Authentication scope management',
    description: 'Create authentication scopes and connect projects to shared app users and sign-in configuration.',
    category: 'administration',
    links: {
      docs: '/docs/override/authentication-scopes/'
    },
    tags: ['authentication', 'access'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['override']
  },
  {
    id: 'app-sso-configuration',
    name: 'App SSO configuration',
    description: 'Configure identity providers and app sign-in settings for an authentication scope in OVERRIDE.',
    category: 'administration',
    links: {
      docs: '/docs/override/configure-sso/'
    },
    tags: ['sso', 'authentication'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['override']
  },
  {
    id: 'project-administration',
    name: 'Project administration',
    description: 'Create projects and manage their organization settings.',
    category: 'administration',
    links: {
      product: '/override/',
      docs: '/docs/override/'
    },
    tags: ['administration'],
    availability: FeatureAvailability.PLAN_DEPENDENT,
    systems: ['override']
  },
  {
    id: 'source-drafts-and-history',
    name: 'Drafts and version history',
    description: 'Work on changes in drafts and review or restore saved versions.',
    category: 'development',
    systems: ['source'],
    links: {
      docs: '/docs/oxide/source-and-drafts/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  },
  {
    id: 'project-builds',
    name: 'Project builds',
    description: 'Build project source and packages for deployment.',
    category: 'development',
    systems: ['build'],
    links: {
      docs: '/docs/oxide/debugging/build-logs/'
    },
    tags: ['development'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  },
  {
    id: 'application-update-delivery',
    name: 'Application update delivery',
    description: 'Deliver released application changes to enrolled apps.',
    category: 'runtime',
    systems: ['updates'],
    links: {
      docs: '/docs/container/app-runtime-and-container-updates/'
    },
    tags: ['runtime'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  },
  {
    id: 'file-and-attachment-storage',
    name: 'File and attachment storage',
    description: 'Store uploaded files and attachments alongside project data.',
    category: 'data',
    systems: ['backend'],
    links: {
      docs: '/docs/concepts/backend-data/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  },
  {
    id: 'device-enrollment',
    name: 'Device enrollment',
    description: 'Enroll devices into the intended deployment and manage their access.',
    category: 'data',
    systems: ['backend'],
    links: {
      docs: '/docs/get-started/tutorial-build-your-first-app/release-your-app/deploy-and-enroll-users/'
    },
    tags: ['data'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  },
  {
    id: 'email-delivery',
    name: 'Email delivery through CloudCode',
    description: 'Send email through an external provider such as SendGrid from a CloudCode task.',
    category: 'automation',
    systems: ['cloudcode'],
    links: {
      docs: '/docs/cloudcode/triggering-a-cloudcode-task/trigger-cc-via-a-webhook/'
    },
    tags: ['email', 'cloudcode', 'integrations'],
    availability: FeatureAvailability.PLAN_DEPENDENT
  }
];
